import type { Theme } from './api';

/**
 * Ein Aufbau, je Anbieter eingestellt (Konzept 9): Grundschema hell/dunkel, Akzentfarbe (optional als Verlauf),
 * Knopffarbe, Schrift. Alles landet als CSS-Variablen auf <html>; die Stylesheets kennen nur die Variablen.
 * Keine externen Schriften – die Plattform lädt nichts von Dritten.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  const scheme = theme.scheme === 'light' ? 'light' : 'dark';
  root.dataset['scheme'] = scheme;

  const brand = isHexColor(theme.brand) ? theme.brand : scheme === 'dark' ? '#f0b431' : '#0f5490';
  const brand2 = isHexColor(theme.brand2) ? theme.brand2 : brand;
  const action = isHexColor(theme.action) ? theme.action : brand;
  root.style.setProperty('--brand', brand);
  root.style.setProperty('--brand-2', brand2);
  root.style.setProperty('--brand-ink', readableOn(brand));
  root.style.setProperty('--brand-soft', withAlpha(brand, scheme === 'dark' ? 0.16 : 0.1));
  root.style.setProperty('--brand-line', withAlpha(brand, scheme === 'dark' ? 0.35 : 0.3));
  root.style.setProperty('--action', action);
  root.style.setProperty('--action-ink', readableOn(action));
  root.style.setProperty('--font', theme.font === 'serif' ? 'var(--font-serif)' : theme.font === 'rounded' ? 'var(--font-rounded)' : 'var(--font-sans)');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', scheme === 'dark' ? '#0b0b0c' : '#ffffff');
}

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

function withAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Weiß oder Schwarz – je nachdem, was auf der Farbe lesbar bleibt. */
function readableOn(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.4 ? '#111111' : '#ffffff';
}
